import { createLive } from '../lib/live.js';
import { PB_URL } from '../lib/config.js';

/**
 * De open lijn met de server aan de app knopen.
 *
 * Elke collectie krijgt hier een handeling: komt er iets binnen over paal_messages,
 * dan halen we de chat op. Meer niet. Het binnenkomende record blijft ongebruikt,
 * want dan hoeft er niets te veranderen aan de manier waarop de app zijn gegevens
 * samenvoegt, en dat scheelt een hele klasse aan fouten.
 *
 * Wel gebundeld: wie twintig pixels in het gastenboek zet, stuurt twintig
 * gebeurtenissen achter elkaar. Die worden één ophaalactie.
 */

/** Zoveel wachten we of er nog meer binnenkomt voor we gaan ophalen. */
const BUNDEL_MS = 250;

export default {
  /** Welke collectie zet welke ophaalactie in gang. */
  liveRoutes() {
      return {
        paal_messages: () => this.fetchChat(),
        [this.collectionName]: () => this.refreshGallery(true),
        paal_duo: () => this.fetchDuo(),
        paal_dm: () => this.fetchDms(),
        paal_comments: () => this.fetchComments(),
        paal_polls: () => this.fetchPoll(),
        paal_requests: () => this.fetchRequests(),
        // De Krabbellijn: zodra iemand een beurt doorgeeft weet je het meteen, en
        // dat kost niets extra's; het abonnement gaat toch de deur uit.
        paal_ketting: () => this.ketHaal(),
        // De arena is het enige spel waarin je op elkaar staat te wachten, dus hier
        // telt elke seconde: een keuze van de ander moet meteen binnenkomen.
        paal_arena: () => this.haalArena(),
        // Het gastenboek is één gedeeld vel; alleen bijwerken als je ernaar kijkt.
        paal_guestbook: () => { if (this.state.gbOpen) this.fetchGuestbook(); },
        // Thema, krabbel van de dag en de ideeënbus lopen samen op.
        paal_themes: () => this.fetchDayData(true),
        paal_daily: () => this.fetchDayData(true),
        paal_feature_requests: () => this.fetchDayData(true)
      };
    },

  /**
   * Een noodrem. Zet ?nolive achter het adres en de app werkt weer volledig op zijn
   * timers, zonder dat er iets uitgerold hoeft te worden. Handig om te vergelijken,
   * en handig als de open lijn zich ooit misdraagt op een raar netwerk.
   */
  liveUit() {
      try { return /(^|[?&])nolive\b/.test(window.location.search); } catch (e) { return false; }
    },

  startLive() {
      if (this.live || !PB_URL || this.liveUit()) return;
      const routes = this.liveRoutes();
      this.liveTimers = {};
      this.live = createLive({
        url: PB_URL,
        // paal_accounts staat er bewust niet bij. Iedereen laat elke vijftien
        // seconden weten dat hij er nog is, dus met vierentwintig collega's zou dat
        // ruim anderhalve gebeurtenis per seconde geven. Ophalen wie er online is
        // blijft daarom gewoon aan de hartslag hangen.
        collections: Object.keys(routes),
        onEvent: (naam, data) => {
          // Een weggehaalde reactie: het ophalen kijkt alleen naar wat er sinds de
          // vorige keer bij kwam, en haalt dus nooit iets weg. Dan stond een reactie
          // die de mod verwijderde bij iedereen nog tot ze herlaadden.
          if (naam === 'paal_comments' && data && data.action === 'delete' && data.record) this.vergeetComment(data.record.id);
          this.onLiveEvent(naam, routes[naam]);
        },
        onStatus: (aan) => { this.liveOn = aan; }
      });
      this.live.start();
    },

  stopLive() {
      if (this.live) this.live.stop();
      this.live = null;
      this.liveOn = false;
      Object.values(this.liveTimers || {}).forEach(clearTimeout);
      this.liveTimers = {};
    },

  onLiveEvent(naam, doe) {
      if (!doe) return;
      // In de achtergrond niets ophalen. Kom je terug, dan haalt onVisible alles
      // in één keer opnieuw op.
      if (typeof document !== 'undefined' && document.hidden) return;
      clearTimeout(this.liveTimers[naam]);
      this.liveTimers[naam] = setTimeout(() => { try { doe(); } catch (e) {} }, BUNDEL_MS);
    },

  /**
   * Mag deze tik van een timer overgeslagen worden?
   *
   * Zolang de lijn openstaat hoeven de timers alleen nog een vangnet te zijn: ze
   * gaan van elke tik naar elke zoveelste. Zakt de lijn weg, dan lopen ze meteen
   * weer op volle snelheid, zonder dat er ergens een timer herstart hoeft te worden.
   */
  liveSkip(tik, factor) {
      return !!this.liveOn && tik % factor !== 0;
    }
};
